import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, MessageCircle, MessageSquareText, Phone, Gift } from "lucide-react";
import { formatPrice, restaurant } from "../../data/siteData";
import { buildOrderMessage, extrasText, smsUrl, whatsappUrl } from "../../lib/orderMessage";

const money = (v) => formatPrice(Math.round(v * 100) / 100);

const Recap = ({ items, total, pizzaCount, details }) => (
  <div className="rounded-2xl border border-[#D4A359]/20 bg-[#0B2216]/50 p-5" data-testid="review-recap">
    <ul className="space-y-3">
      {items.map((i) => (
        <li key={i.key} className="flex items-start justify-between gap-3 text-sm" data-testid={`review-line-${i.id}`}>
          <span className="text-[#F7F4EB]">
            {i.qty} × {i.product.name}
            {extrasText(i) && <span className="block text-xs text-[#A2B3A7]">{extrasText(i)}</span>}
          </span>
          <span className="shrink-0 text-[#E5B66C]">{money(i.total)}</span>
        </li>
      ))}
    </ul>
    <p className="mt-4 pt-4 border-t border-[#D4A359]/15 flex items-baseline justify-between">
      <span className="text-sm uppercase tracking-[0.2em] text-[#D4A359]">Total TTC</span>
      <span className="font-display text-3xl text-[#F7F4EB]" data-testid="review-total">{money(total)}</span>
    </p>
    {pizzaCount > 0 && <p className="mt-2 flex items-center gap-2 text-sm text-[#F0A385]" data-testid="review-cans"><Gift className="w-4 h-4" /> {pizzaCount} canette{pizzaCount > 1 ? "s" : ""} offerte{pizzaCount > 1 ? "s" : ""}</p>}
    <dl className="mt-4 grid gap-1 text-sm text-[#D6CFC0]">
      <div data-testid="review-pickup"><dt className="inline text-[#A2B3A7]">Retrait : </dt><dd className="inline text-[#F7F4EB]">{details.day.full} à {details.slot.label}</dd></div>
      <div data-testid="review-name"><dt className="inline text-[#A2B3A7]">Nom : </dt><dd className="inline text-[#F7F4EB]">{details.name}</dd></div>
      <div data-testid="review-phone"><dt className="inline text-[#A2B3A7]">Téléphone : </dt><dd className="inline text-[#F7F4EB]">{details.phone}</dd></div>
      {details.notes && <div data-testid="review-notes"><dt className="inline text-[#A2B3A7]">Remarques : </dt><dd className="inline text-[#F7F4EB]">{details.notes}</dd></div>}
    </dl>
  </div>
);

const Fallback = ({ message }) => (
  <div className="rounded-2xl border border-[#D4A359]/20 p-5" data-testid="review-fallback">
    <p className="text-sm text-[#F7F4EB]">WhatsApp ne s’ouvre pas ou vous ne l’avez pas ?</p>
    <div className="mt-4 flex flex-col sm:flex-row gap-3">
      <a href={smsUrl(message)} className="btn-ghost justify-center !py-2.5 text-sm" data-testid="review-sms-button"><MessageSquareText className="w-4 h-4" /> Envoyer par SMS</a>
      <a href={restaurant.phoneHref} className="btn-ghost justify-center !py-2.5 text-sm" data-testid="review-call-button"><Phone className="w-4 h-4" /> Appeler le {restaurant.phone}</a>
    </div>
    <p className="mt-3 text-xs text-[#A2B3A7]">Le SMS contient le même message, à envoyer vous-même depuis votre téléphone.</p>
  </div>
);

export const OrderReview = ({ items, total, pizzaCount, details, onEdit }) => {
  const [opened, setOpened] = useState(false);
  const message = buildOrderMessage({ items, total, pizzaCount, details });
  const onSend = (e) => {
    if (new Date(details.slot.value).getTime() <= Date.now()) {
      e.preventDefault();
      toast.error("Ce créneau de retrait est dépassé. Merci d’en choisir un autre.");
      onEdit();
      return;
    }
    setOpened(true);
  };
  return (
    <div className="mt-6 space-y-5" data-testid="order-review">
      <h3 className="font-display text-2xl">Vérifiez votre commande</h3>
      <Recap items={items} total={total} pizzaCount={pizzaCount} details={details} />
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4A359]">Message qui sera envoyé au restaurant</p>
        <pre className="mt-2 whitespace-pre-wrap break-words rounded-2xl border border-[#D4A359]/20 bg-[#0B2216]/70 p-4 font-sans text-sm text-[#D6CFC0]" data-testid="review-message-preview">{message}</pre>
      </div>
      <p className="flex items-start gap-2 rounded-2xl border border-[#D4A359]/40 bg-[#D4A359]/10 p-4 text-sm text-[#F7F4EB]" data-testid="review-not-confirmed-notice">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-[#D4A359]" />
        <span>Le bouton ouvre WhatsApp avec ce message déjà rédigé : appuyez ensuite sur « Envoyer » dans WhatsApp. Votre commande n’est confirmée que lorsque le restaurant vous a répondu. Paiement au restaurant lors du retrait.</span>
      </p>
      <a href={whatsappUrl(message)} target="_blank" rel="noopener noreferrer" onClick={onSend} className="btn-gold w-full justify-center" data-testid="send-order-whatsapp-button">
        <MessageCircle className="w-4 h-4" /> Envoyer ma commande
      </a>
      {opened && (
        <p className="rounded-2xl border border-[#8FAD95]/40 bg-[#8FAD95]/10 p-4 text-sm text-[#F7F4EB]" data-testid="review-after-open-notice">
          WhatsApp s’est ouvert ? Vérifiez que le message a bien été envoyé. Tant que CAP SUD ne vous a pas répondu, votre commande n’est pas confirmée.
        </p>
      )}
      <Fallback message={message} />
      <button type="button" onClick={onEdit} className="inline-flex items-center gap-2 text-sm text-[#E5B66C] hover:text-[#F7F4EB]" data-testid="review-edit-button">
        <ArrowLeft className="w-4 h-4" /> Modifier ma commande
      </button>
    </div>
  );
};
